/**
 * Metodo hungaro (Kuhn-Munkres) por caminos aumentantes con potenciales.
 *
 * Resuelve el problema de asignacion en O(n^2 * m): dada una matriz de costes
 * agente x tarea, elige que agente hace que tarea de forma que el coste TOTAL
 * sea minimo. No es lo mismo que "cada uno a lo suyo mas barato": el optimo
 * global a veces obliga a un agente a coger una tarea peor para que el conjunto
 * salga ganando, y eso es justo lo que le falta a un marcaje codicioso.
 *
 * En Golazo los tamanos son ridiculos (como mucho 10x10), asi que el coste de
 * calculo es irrelevante; lo que importa es la calidad de la asignacion.
 */

/** Coste prohibitivo: la celda existe pero no queremos usarla salvo obligados. */
export const FORBIDDEN = 1e9;

/**
 * @param cost matriz `filas x columnas` (agentes x tareas). Puede no ser cuadrada.
 * @returns para cada fila, la columna asignada, o `-1` si se queda sin tarea
 *   (ocurre cuando hay mas agentes que tareas).
 */
export function solveAssignment(cost: readonly (readonly number[])[]): number[] {
  const rows = cost.length;
  if (rows === 0) return [];
  const cols = cost[0]!.length;
  if (cols === 0) return new Array(rows).fill(-1);

  // El algoritmo necesita filas <= columnas. Si sobran agentes se transpone,
  // se resuelve, y se da la vuelta al resultado.
  if (rows > cols) {
    const transposed: number[][] = Array.from({ length: cols }, (_, j) =>
      Array.from({ length: rows }, (_, i) => cost[i]![j]!),
    );
    const solvedT = solveAssignment(transposed);
    const result: number[] = new Array(rows).fill(-1);
    for (let j = 0; j < cols; j++) {
      const i = solvedT[j]!;
      if (i >= 0) result[i] = j;
    }
    return result;
  }

  const INF = Infinity;
  // Indexado desde 1: la fila/columna 0 es el centinela del camino aumentante.
  const u = new Array<number>(rows + 1).fill(0); // potencial de fila
  const v = new Array<number>(cols + 1).fill(0); // potencial de columna
  const match = new Array<number>(cols + 1).fill(0); // columna -> fila asignada
  const path = new Array<number>(cols + 1).fill(0); // reconstruccion del camino

  for (let row = 1; row <= rows; row++) {
    match[0] = row;
    let col = 0;
    const minCost = new Array<number>(cols + 1).fill(INF);
    const used = new Array<boolean>(cols + 1).fill(false);

    // Camino aumentante: se extiende hasta topar con una columna libre.
    do {
      used[col] = true;
      const curRow = match[col]!;
      let delta = INF;
      let nextCol = 0;

      for (let j = 1; j <= cols; j++) {
        if (used[j]) continue;
        const reduced = cost[curRow - 1]![j - 1]! - u[curRow]! - v[j]!;
        if (reduced < minCost[j]!) {
          minCost[j] = reduced;
          path[j] = col;
        }
        if (minCost[j]! < delta) {
          delta = minCost[j]!;
          nextCol = j;
        }
      }

      // Se reajustan los potenciales para que aparezca una arista de coste 0.
      for (let j = 0; j <= cols; j++) {
        if (used[j]) {
          u[match[j]!] = u[match[j]!]! + delta;
          v[j] = v[j]! - delta;
        } else {
          minCost[j] = minCost[j]! - delta;
        }
      }
      col = nextCol;
    } while (match[col] !== 0);

    // Se invierte el camino: cada columna del camino pasa a su nueva fila.
    do {
      const prev = path[col]!;
      match[col] = match[prev]!;
      col = prev;
    } while (col !== 0);
  }

  const assignment = new Array<number>(rows).fill(-1);
  for (let j = 1; j <= cols; j++) {
    const row = match[j]!;
    if (row > 0) assignment[row - 1] = j - 1;
  }
  return assignment;
}

/** Coste total de una asignacion. Util para tests y depuracion. */
export function assignmentCost(
  cost: readonly (readonly number[])[],
  assignment: readonly number[],
): number {
  let total = 0;
  for (let i = 0; i < assignment.length; i++) {
    const j = assignment[i]!;
    if (j >= 0) total += cost[i]![j]!;
  }
  return total;
}
